import type {
  ChannelSelection,
  ConfigReq,
  DatasetSelectionReq,
  IsaacEvaluationReq,
  JsonObject,
  PreviewOverridesReq,
  ResourcesReq,
  SubmitTrainingSessionReq,
  TrainingInitReq,
  ValidateTrainingSessionReq,
} from "@/api";
import { buildNotificationOverride } from "@/api";
import { formatBranchHash, shortSha, type BranchHashParts } from "@/components/table/branchHash";
import { getAtPath, parseListOrScalar } from "./recipe/recipeCoerce";
import { formatOverrideKey, parseOverrideKey } from "./recipe/recipeOverrides";
import { randomBackgroundDeleteOverrideKeysFromYaml } from "./recipe/recipeRandomBackground";
import {
  BUDGET_TOTAL_KEY,
  IMAGE_SIZE_OVERRIDE_KEY,
  imageSizeAt,
  parseCount,
  SAVE_EVERY_N_EVALS_KEY,
  SAVE_TOP_K_KEY,
  saveEveryNEvalsForLastOnly,
} from "./recipe/recipeSeed";
import { recipeValueAt } from "./recipe/recipeFlatten";
import { applyOverridesToYaml, normalizeYaml, parseRecipe } from "./recipe/recipeYaml";
import { patchOverridesIntoYaml } from "./recipe/recipeYamlPatch";
import { isBranchRef } from "./scheduleDraft";
import {
  SIGMA_WRISTS,
  SIGMA_LEGACY_WRISTS,
  SIGMA_CAMERA_PATH,
  type SigmaDefaultsState,
} from "./sigmaDatasetDefaults";

// New Training 위저드의 순수 폼 상태, 검증, payload 빌더.

export type KeyValuePair = { key: string; value: string };

// 학습 입력 하나. uuid 는 항상 공용 Dataset uuid 다. 종류 축은 없다.
// 담은 Dataset 카드가 그리는 표시 정보. payload 에는 들어가지 않는다. 담는 쪽이 이미 받은 응답
// (picker 의 목록, 재제출·재학습 프리필의 세션 상세)에서 만들어 전달하므로 추가 요청이 없다. 없으면
// 카드는 이름만 표시한다(데이터셋 표면에서 바로 여는 진입).
export type SubmitDatasetPreview = {
  thumbnailUrl: string | null;
  // 이름 아래 요약(팔 구성 · 카메라 수 · 에피소드 수). picker 목록 행과 같은 문구를 쓴다.
  // 출처 응답에 요약이 없으면 빈 배열이고, 카드는 그 줄을 그리지 않는다.
  meta: readonly string[];
};

export type SubmitDatasetSelection = {
  uuid: string;
  // confirm 요약·picker 표시용 이름(payload 에는 들어가지 않는다).
  label: string;
  preview?: SubmitDatasetPreview;
  // train 은 학습 입력, val 은 검증 입력이다. BE 는 reference 를 열지 않는다.
  role: "train" | "val";
  // 멀티 train 샘플링 비율. null 이면 payload 에서 키를 빼고 training 이 균등 배분으로 정한다.
  // 0 은 유효한 값이 아니라 BE 가 422 를 주므로, 미지정과 구분해서 null 로 둔다.
  weight: number | null;
  // 배경 분리에 쓸 어노테이션 세트(선택). 세트는 특정 Dataset 에 묶여 있어 입력별 값이다.
  // null 은 미사용이고, 그때 payload 에서 키를 뺀다.
  annotationSetUuid: string | null;
};

// finetune 시작점으로 고른 카탈로그 Policy. 이름은 확정 요약 표시용이라 payload 에는 들어가지 않는다.
// learning-core 출처(브랜치·커밋)는 "이 Commit 으로 맞추기" 제안이 읽는 값이다. 카탈로그가 등록 시점에
// 비정규화한 provenance 라 없을 수 있다(null).
export type SubmitInitPolicy = {
  id: string;
  name: string;
  learningCoreRef: string | null;
  learningCoreSha: string | null;
};

// retrain 시작점인 소스 세션. title 도 표시용이라 payload 에는 들어가지 않는다. 세션 id 만 보낸다.
export type SubmitInitSourceSession = { id: string; title: string };

// 학습 시작점 세 축. 어느 축도 checkpoint_uri 를 받지 않는다. 직접 지정은 registry 가 가시성을
// 판정할 수 없어서 BE 가 막는다. retrain 은 소스 세션 id 만 보내고 서버가 URI 를 해석한다.
//
// finetune 을 켜고 아직 Policy 를 고르지 않은 중간 상태를 `policy: null` 로 표현한다. 이 상태를
// 없애고 "Policy 가 있으면 finetune" 으로 합치면, 켜자마자 리소스 잠금·config 교체를 적용할
// 방법이 없고 빈 uuid 를 payload 에 넣을 위험이 생긴다. retrain 에는 같은 중간 상태가 없다.
// 소스는 진입점(세션 상세)이 정하고 위저드 안에서는 고를 수 없다.
export type SubmitInitState =
  | { strategy: "scratch" }
  | { strategy: "finetune"; policy: SubmitInitPolicy | null }
  | { strategy: "retrain"; source: SubmitInitSourceSession };

export type SubmitConfigState = {
  // repo_config 소스로 쓰는 config 파일 이름.
  configName: string;
  // 서버에서 받은 레포 원본 본문. 편집 diff 의 기준이고 override 적용 대상이라 편집으로 덮어쓰지 않는다.
  inlineYaml: string;
  // 본문 직접 편집 방식의 본문. null 이면 overrides 방식이다(isDirectConfigEdit). 두 방식은 배타라
  // 두 입력 위치가 같은 값을 덮어쓰지 않는다. 원본과 내용이 같으면 repo_config 로 제출한다(hasConfigEdit).
  editedYaml: string | null;
  // 사용자 입력과 Dataset wrist 기본값의 override(dot-notation 키-값).
  // 전용 컨트롤의 자동 override 는 제출/diff 계산 때 뒤에 추가한다.
  // 직접 편집 중에는 빈 목록이다. 진입할 때 본문에 반영하고(bakeOverridesIntoYaml), 돌아올 때
  // 컨트롤러가 스냅샷을 복원한다.
  overrides: KeyValuePair[];
  // 끄면 RandomBackground transform 을 삭제하는 Hydra override 를 자동 추가한다.
  randomBackgroundEnabled: boolean;
  // 비전 인코더 입력 해상도 `[H, W]`. null 은 미지정이고 config 값을 그대로 쓴다. 이때 제출에
  // override 를 넣지 않는다. 값이 있으면 config 값과 다를 때만 자동 override 로 보낸다.
  imageSize: { height: number; width: number } | null;
  // 켜면 체크포인트를 마지막 하나만 남긴다. 값은 예산에서 계산하므로 저장하지 않는다.
  saveOnlyLastCheckpoint: boolean;
};

// 상류 `ConfigReq.inline_yaml` 한도(`session_schema.py`). 넘으면 BE 가 422 를 주므로 폼에서 먼저 막는다.
export const INLINE_YAML_MAX_BYTES = 1024 * 1024;

// 편집이 실제로 원본과 다른지 판정한다. 열기만 하고 닫은 편집은 무편집으로 본다. 그래야 제출이
// wire 를 바꾸지 않는다. 원본 본문과 편집본을 같은 규칙으로 정규화해서 개행·끝 공백 차이를 무시한다.
export function hasConfigEdit(config: SubmitConfigState): boolean {
  return (
    config.editedYaml !== null &&
    normalizeYaml(config.editedYaml) !== normalizeYaml(config.inlineYaml)
  );
}

// 본문 직접 편집 방식인지. editedYaml 이 있으면 그 방식이고, 그때 사용자 override 목록은 비어 있다
// (컨트롤러 enterDirectEdit 가 보장한다). 화면의 스위처·요약·내비가 모두 이 술어 하나를 읽는다.
export function isDirectConfigEdit(config: SubmitConfigState): boolean {
  return config.editedYaml !== null;
}

// 제출·미리보기·검증이 모두 읽는 "현재 본문". 편집이 있으면 편집본, 없으면 레포 원본이다.
export function activeConfigYaml(config: SubmitConfigState): string {
  return hasConfigEdit(config) && config.editedYaml !== null
    ? config.editedYaml
    : config.inlineYaml;
}

export function inlineYamlByteLength(yaml: string): number {
  return new TextEncoder().encode(yaml).length;
}

// 폼의 채널 선택은 wire 계약(ChannelSelection, api/channelSelection.ts)을 그대로 쓴다. topic 별
// 3-상태(null=위임/[]=명시적 빈/배열=선택)를 폼이 다시 합치면 위임이 빈 선택으로 바뀐다.
// 선택 전체가 null 이면 제안을 아직 시드하지 않은 상태다. payload 에서 생략되고, BE 가 제출할 때
// 기본 제안으로 암묵적으로 재조립한다. 값의 생성·수정은 api/channelSelection 헬퍼만 쓴다.

export type SubmitFormState = {
  // 자동 설정의 소유권이다. 제출 payload에는 포함하지 않는다.
  sigmaDefaults?: SigmaDefaultsState;
  // 학습 입력 1개 이상. **순서가 곧 targetDatasetIndex 가 세는 축**이라 목록으로 가진다.
  datasets: SubmitDatasetSelection[];
  // 학습 타깃(datasets 의 0-based 위치). train 이 2개 미만이면 BE 가 422 를 주므로 payload 에서 뺀다.
  targetDatasetIndex: number | null;
  // 학습 시작점. scratch 가 기본 동작이다.
  init: SubmitInitState;
  workspaceId: string;
  experimentId: string;
  experimentName: string | null;
  // learning-core 출처는 두 축이다. `branchRef` 는 고른 브랜치 이름이고, `commitPin` 은 그 위에
  // 추가하는 고정이다. `null` 이면 브랜치 최신(tip)을 따르고, 문자열이면 그 full SHA 로 고정한다.
  // 제출 ref 는 `learningCoreRef` 하나가 정한다. `?? ""` 로 합치면 "최신 추적"이 "빈 고정"이 되어
  // 게이트가 알림 없이 닫힌다(§16).
  branchRef: string;
  commitPin: string | null;
  config: SubmitConfigState;
  channelSelection: ChannelSelection | null;
  nodes: number;
  gpusPerNode: number;
  resourceExtra: KeyValuePair[];
  // 스팟 노드 허용 3-상태. `null` 은 "클러스터 기본을 따름"이고, 제출에서 키를 뺀다.
  // FE 는 상류 기본을 알 수 없기 때문이다(BE `ResourceDefaultsView` 는 이 축에 시드할 값이
  // 없다고 명시한다). 신규 제출은 `false` 로 열어서 "화면의 노브가 곧 실제 값"을 유지한다.
  // 미지정으로 제출된 원본을 재제출할 때만 `null` 로 열어서 원본 상태를 보존한다.
  allowSpot: boolean | null;
  // 노드 잔여 수명 하한(초). 스팟을 켜면 이 값이 회수 위험을 제어한다.
  minLifetimeSeconds: number;
  // 자동 재시도가 직전 attempt 의 체크포인트에서 이어서 학습할지. finetune 제출에서는 상류가 알리지
  // 않고 무시하므로 폼이 노브를 숨기고 payload 도 false 로 바꾼다(buildSubmitReq).
  resumeOnRetry: boolean;
  // LR 자동 계산(선형 스케일) 토글. override 행 갱신은 컨트롤러가 소유한다.
  autoLr: boolean;
  displayName: string;
  // 학습이 끝나면 알림을 보낼지. 화면에 노브가 없는 축이다. 값은 Training 헤더 토글이
  // 정하고(상류 수신자 행이 정본), 컨트롤러가 내 스코프를 확정할 때 시드한다. 꺼져 있으면
  // payload 에서 키를 빼고 상류 기본 설정에 맡긴다.
  notifyOnFinish: boolean;
};

export function emptyConfigState(): SubmitConfigState {
  return {
    configName: "",
    inlineYaml: "",
    editedYaml: null,
    overrides: [],
    randomBackgroundEnabled: true,
    imageSize: null,
    saveOnlyLastCheckpoint: false,
  };
}

export function emptySubmitForm(defaults: { nodes: number; gpusPerNode: number }): SubmitFormState {
  return {
    datasets: [],
    targetDatasetIndex: null,
    init: { strategy: "scratch" },
    workspaceId: "",
    experimentId: "",
    experimentName: null,
    branchRef: "",
    commitPin: null,
    config: emptyConfigState(),
    channelSelection: null,
    nodes: defaults.nodes,
    gpusPerNode: defaults.gpusPerNode,
    resourceExtra: [],
    allowSpot: false,
    minLifetimeSeconds: MIN_LIFETIME_SECONDS_DEFAULT,
    resumeOnRetry: false,
    autoLr: false,
    displayName: "",
    notifyOnFinish: false,
  };
}

// 세 스텝 위저드: 데이터셋 선택 → 구성(Isaac evaluation 은 그 안의 섹션) → 확정.
export const SUBMIT_STEPS = ["dataset", "configure", "confirm"] as const;
// "새 Schedule" 위저드(mode:"schedule")는 확정 앞에 Schedule 스텝 하나를 추가한다. 같은
// 제출 payload 에 반복 축·실행 게이트만 추가하는 흐름이라 앞 두 스텝을 그대로 공유한다.
export const SCHEDULE_SUBMIT_STEPS = ["dataset", "configure", "schedule", "confirm"] as const;
export type SubmitStep = (typeof SCHEDULE_SUBMIT_STEPS)[number];

export const SUBMIT_STEP_LABELS: Record<SubmitStep, string> = {
  dataset: "Dataset 선택",
  configure: "Training 구성",
  schedule: "Schedule 설정",
  confirm: "확정",
};

// 각 스텝의 필수 입력 게이트('다음' 활성 조건). confirm 은 항상 통과한다(제출 버튼이 별도 게이트다).
// 구성 스텝의 Isaac Evaluation 준비 여부는 이미지 메타가 비동기라 폼이 아니라 컨트롤러가 추가한다.
// schedule 스텝의 초안도 폼 밖(컨트롤러 소유)이라 여기서는 항상 통과한다. 게이트는 컨트롤러의
// canLeaveStep 이 isScheduleDraftReady 로 추가한다.
export function isStepComplete(step: SubmitStep, form: SubmitFormState): boolean {
  switch (step) {
    case "dataset":
      // 첫 스텝은 학습 데이터만 고른다. 담을 곳(워크스페이스·실험)은 구성 스텝이 소유한다.
      return hasTrainDataset(form.datasets);
    case "configure":
      // 담을 곳·시작점·레시피·리소스가 모두 정해져야 확정으로 넘어간다.
      return (
        isExperimentComplete(form) &&
        isInitComplete(form) &&
        isRecipeComplete(form) &&
        isResourcesComplete(form)
      );
    case "schedule":
      return true;
    case "confirm":
      return true;
  }
}

// 세션을 담을 곳이 정해졌는지. 워크스페이스는 admin 매핑이라 고르기만 할 수 있고, 실험은 그
// 워크스페이스 안에서 고르거나 새로 만든다.
export function isExperimentComplete(form: SubmitFormState): boolean {
  return form.workspaceId !== "" && form.experimentId !== "";
}

// 제출·미리보기·검증이 보내는 learning-core ref. 고정한 커밋이 있으면 그 SHA, 없으면 브랜치 이름이다.
// 커밋 고정은 `learning_core={kind: branch, ref: <40-hex>}` 로 보낸다(상류 빌더는 40-hex 를 해석 없이
// 그대로 쓴다. `kind` 에 commit 이 따로 없다). 세 payload 와 게이트가 모두 이 함수를 거쳐야 화면의
// diff·검증이 실제 제출물과 어긋나지 않는다.
export function learningCoreRef(form: Pick<SubmitFormState, "branchRef" | "commitPin">): string {
  return form.commitPin ?? form.branchRef;
}

export function isCommitPinned(form: Pick<SubmitFormState, "commitPin">): boolean {
  return form.commitPin !== null;
}

// 이 폼이 Branch 의 최신 Commit 을 추종할 수 있는지. 제출 ref 가 브랜치 이름이어야 한다(아직
// 고르지 않았거나 커밋을 고정했으면 상류가 422 로 거절한다). Schedule 스텝의 토글 잠금과 제출할 때의
// 게이트 마스킹이 같은 술어를 읽어야, 화면이 잠근 값이 wire 로 전달되지 않는다.
export function canFollowBranchTip(
  form: Pick<SubmitFormState, "branchRef" | "commitPin">,
): boolean {
  return isBranchRef(learningCoreRef(form));
}

// 화면과 문자열 요약이 같은 출처를 사용한다. tip 을 모르는 요약은 null 을 전달한다.
export function codeSourceParts(
  form: Pick<SubmitFormState, "branchRef" | "commitPin">,
  tipSha: string | null,
): BranchHashParts {
  const fullHash = form.commitPin ?? tipSha;
  return { branch: form.branchRef, hash: shortSha(fullHash), fullHash };
}

export function codeSourceLabel(
  form: Pick<SubmitFormState, "branchRef" | "commitPin">,
  tipSha: string | null,
): string {
  const { branch, hash } = codeSourceParts(form, tipSha);
  return formatBranchHash(branch, hash);
}

// 레시피 완성 여부. learning-core ref 와 선택한 config 이름이 필요하다. 브랜치 없이 커밋만 고정된
// 상태(40-hex ref 로 제출한 세션의 재제출)도 유효한 제출이다. Random background 제거는
// 본문에서 자동 delete override 키를 만들 수 있을 때만 완성으로 본다. 편집본은 상류 한도 안에
// 있어야 한다. 넘으면 제출에서 422 가 나므로 폼에서 먼저 막는다.
export function isRecipeComplete(form: SubmitFormState): boolean {
  const config = form.config;
  if (learningCoreRef(form) === "") {
    return false;
  }
  if (config.configName === "") {
    return false;
  }
  if (configEditError(config) !== null) {
    return false;
  }
  return config.randomBackgroundEnabled || randomBackgroundDeleteRows(config).length > 0;
}

/**
 * 편집본이 제출을 막는 이유. 없으면 `null`.
 *
 * YAML 파싱 실패는 여기서 판정하지 않는다. 파싱 결과는 컨트롤러가 가지고, 구성 스텝의 '다음'
 * 게이트(`canLeaveStep`)가 그 판정을 맡는다. 여기서는 폼만으로 판정할 수 있는 크기 한도를 확인한다.
 */
export function configEditError(config: SubmitConfigState): string | null {
  if (!hasConfigEdit(config)) {
    return null;
  }
  const bytes = inlineYamlByteLength(activeConfigYaml(config));
  if (bytes > INLINE_YAML_MAX_BYTES) {
    const mib = (bytes / (1024 * 1024)).toFixed(2);
    return `편집한 config 본문이 ${mib}MiB 로 제출 한도(1MiB)를 넘었습니다.`;
  }
  return null;
}

// 리소스 완성 여부. 노드·GPU 가 최소 1 이상이어야 한다. finetune 은 항상 단일 프로세스라 정확히 1×1 이어야
// 한다(learning-core fine-tune 진입은 DDP 불가). 폼이 값을 잠그므로 이 검사는 방어용이다.
export function isResourcesComplete(form: SubmitFormState): boolean {
  if (form.nodes < 1 || form.gpusPerNode < 1) {
    return false;
  }
  return form.init.strategy === "scratch" || !finetuneResourcesInvalid(form);
}

// BE init_resources_invalid 의 사본. finetune 은 1 node × 1 GPU 만 허용한다(그 외 400).
export function finetuneResourcesInvalid(form: SubmitFormState): boolean {
  return form.nodes !== 1 || form.gpusPerNode !== 1;
}

// 시작점이 정해졌는지. finetune 을 켜고 Policy 를 고르지 않으면 제출할 수 없다. 이 게이트가 없으면
// 사용자가 의도한 finetune 이 알림 없이 scratch 로 제출된다. retrain 은 소스 없이는 고를 수 없어서
// 항상 완성이다.
export function isInitComplete(form: SubmitFormState): boolean {
  return form.init.strategy !== "finetune" || form.init.policy !== null;
}

// BE _INIT_RESERVED_OVERRIDE_KEYS 의 사본. finetune 제출에서 플랫폼이 채우거나 resume 전용이라
// 직접 지정할 수 없는 키들이다. 위반하면 400 이다.
const INIT_RESERVED_OVERRIDE_KEYS: ReadonlySet<string> = new Set([
  "policy.fine_tune_path",
  "train.resume",
  "train.resume_path",
  "train.resume_mlflow_session",
  "train.resume_reset_lr_scheduler",
]);

// finetune 에서 금지된 override 키의 위반 항목. 접두사 해석은 parseOverrideKey 가 소유한다.
// 정규식 사본을 두면 `++`·`~` 처리가 한쪽만 바뀐다. op 는 구분하지 않고 path 로만 비교한다.
// 예약 키는 set·add·delete 중 무엇이든 바꾸는 것 자체가 400 이라 `++policy.fine_tune_path`
// 로 우회되지 않는다(BE 도 같은 규칙으로 판정한다).
export function reservedInitOverrideKeys(overrides: readonly KeyValuePair[]): string[] {
  return overrides
    .map((pair) => pair.key.trim())
    .filter((key) => INIT_RESERVED_OVERRIDE_KEYS.has(parseOverrideKey(key).path));
}

// 학습에 쓸 입력이 있는지. val 만 골라 두면 BE 가 datasets_no_train_role 로 거부한다.
export function hasTrainDataset(datasets: readonly SubmitDatasetSelection[]): boolean {
  return datasets.some((dataset) => dataset.role === "train");
}

// 입력이 하나뿐이면 역할을 고를 필요가 없다. val 하나만 담으면 BE 가 datasets_no_train_role
// 로 거부하므로 고를 수 있는 값은 train 뿐이다. 폼이 그 상태를 고정하고 컨트롤도 잠근다.
export function isRoleLocked(datasets: readonly SubmitDatasetSelection[]): boolean {
  return datasets.length === 1;
}

// 잠긴 상태를 값에도 반영한다. 컨트롤만 잠그면 val 하나가 남는 순간(train 을 빼면 생긴다)
// 화면은 train 을 표시하지만 payload 는 val 로 전달되어 422 가 난다.
export function withLockedRole(
  datasets: readonly SubmitDatasetSelection[],
): SubmitDatasetSelection[] {
  if (!isRoleLocked(datasets)) {
    return [...datasets];
  }
  return datasets.map((dataset) =>
    dataset.role === "train" ? dataset : { ...dataset, role: "train" },
  );
}

// 같은 Dataset 을 두 번 담았을 때 세트 선택을 첫 등장 값으로 합친다.
//
// 만들어진 mask 는 그 Dataset 트리의 `masks/` 한 자리에 놓이므로 한 세션에서 같은 Dataset 이
// 서로 다른 세트를 쓸 수 없다. 상류는 이 모순을 막지 않고 알림 없이 한쪽만 적용한다. registry BE 는
// 경계에서 422(`annotation_set_conflict`)로 막는다. 여기서 값을 묶어서 모순이 생기지 않게
// 한다. 합치는 규칙은 BE `_first_choice_by_dataset` 와 같은 **첫 등장 우선**이다.
//
// 이 함수는 담기·프리필로 들어온 목록의 **정규화**다. 카드에서 값을 바꾸는 경로는 같은 Dataset 의
// 모든 카드에 새 값을 적용해서(SelectedDatasetList) 방금 고른 값이 첫 카드 값으로 되돌려지지 않게 한다.
export function withSyncedAnnotationSets(
  datasets: readonly SubmitDatasetSelection[],
): SubmitDatasetSelection[] {
  const firstChoice = new Map<string, string | null>();
  for (const dataset of datasets) {
    if (!firstChoice.has(dataset.uuid)) {
      firstChoice.set(dataset.uuid, dataset.annotationSetUuid);
    }
  }
  return datasets.map((dataset) => {
    const shared = firstChoice.get(dataset.uuid) ?? null;
    return dataset.annotationSetUuid === shared
      ? dataset
      : { ...dataset, annotationSetUuid: shared };
  });
}

// 타깃을 지정할 수 있는지. 샘플링 앵커는 train 이 2개 이상일 때만 뜻이 있다(BE
// target_index_needs_two_train). 2개 미만이면 폼에서 노브를 숨긴다.
export function canSelectTargetDataset(datasets: readonly SubmitDatasetSelection[]): boolean {
  return datasets.filter((dataset) => dataset.role === "train").length >= 2;
}

// 폼 전체 제출 가능 여부(모든 필수 게이트 통과).
export function canSubmitForm(form: SubmitFormState): boolean {
  return isStepComplete("dataset", form) && isStepComplete("configure", form);
}

// 제출 결과로 학습이 받을 config 본문의 근사치. 확정 스텝의 "원본 대비 변경" diff 시각화용이다.
// buildConfigReq 와 같은 의미 규칙으로 사용자 overrides 와 자동 RandomBackground 삭제 override 를 반영한다.
export function buildEffectiveYaml(config: SubmitConfigState): string {
  return applyOverridesToYaml(
    activeConfigYaml(config),
    keyValuePairsToObject(effectiveConfigOverridePairs(config)),
  );
}

export function effectiveConfigOverridePairs(config: SubmitConfigState): KeyValuePair[] {
  return [...config.overrides, ...automaticConfigOverrides(config)];
}

// overrides → 본문 직접 편집으로 바꿀 때 사용자 행을 본문에 반영한다. 자동 노브 행(RandomBackground·
// 해상도·체크포인트)은 반영하지 않는다. 직접 편집 중에도 노브가 동작해서 제출할 때 본문 위에 다시
// 계산된다. 반영해 두면 노브를 끈 뒤에도 값이 본문에 남는다. 파싱할 수 없는 본문은 그대로 반환되어
// 행이 알림 없이 사라진다. 그래서 호출 전에 컨트롤러가 원본을 읽을 수 있는지 먼저 확인한다.
// 결과는 사용자가 편집할 본문이다. 그래서 행이 바꾸는 줄만 바꾸고 다른 줄은 원문과 같게 둔다.
// 주석의 들여쓰기가 바뀌면, 주석으로 막아 둔 설정을 풀었을 때 다른 부모 밑에 붙는다.
export function bakeOverridesIntoYaml(config: SubmitConfigState): string {
  return patchOverridesIntoYaml(activeConfigYaml(config), keyValuePairsToObject(config.overrides));
}

// RandomBackground 를 지우는 delete override. 목록 인덱스(`~dataset.image_transforms.N`)라
// **제출될 본문**에서 만들어야 한다. 편집으로 transform 순서가 바뀌면 원본 기준 인덱스가 다른
// 항목을 지운다. 이 행을 만들 수 있는지가 곧 "끌 수 있는지"라서 완성 게이트가 따로 확인한다.
export function randomBackgroundDeleteRows(config: SubmitConfigState): KeyValuePair[] {
  if (config.randomBackgroundEnabled) {
    return [];
  }
  return randomBackgroundDeleteOverrideKeysFromYaml(activeConfigYaml(config)).map((key) => ({
    key,
    value: "",
  }));
}

// 폼의 전용 컨트롤이 만드는 override 행. 사용자 override 뒤에 추가되어 같은 계약으로 전달된다.
export function automaticConfigOverrides(config: SubmitConfigState): KeyValuePair[] {
  const rows = randomBackgroundDeleteRows(config);
  const imageSize = config.imageSize;
  const saveEvery = saveOnlyLastCheckpointSaveEvery(config);
  if (imageSize === null && saveEvery === null) {
    return rows;
  }
  const configData = activeConfigData(config);
  // 해상도는 제출될 본문의 값과 다를 때만 보낸다. 화면이 config 값을 자동 입력하므로,
  // 항상 보내면 같은 값의 override 가 diff·preview 에 노이즈로 남는다.
  if (imageSize !== null && !imageSizeMatchesConfig(configData, imageSize)) {
    rows.push({
      key: knobOverrideKey(configData, IMAGE_SIZE_OVERRIDE_KEY),
      value: `[${imageSize.height}, ${imageSize.width}]`,
    });
  }
  if (saveEvery !== null) {
    rows.push({ key: knobOverrideKey(configData, SAVE_TOP_K_KEY), value: "0" });
    rows.push({
      key: knobOverrideKey(configData, SAVE_EVERY_N_EVALS_KEY),
      value: String(saveEvery),
    });
  }
  return rows;
}

// 제출될 본문(편집 반영)의 트리. 읽을 수 없으면 null 이다.
function activeConfigData(config: SubmitConfigState): JsonObject | null {
  const parsed = parseRecipe(activeConfigYaml(config));
  return parsed.errors.length > 0 ? null : parsed.data;
}

// 노브 행의 키. 본문이 선언한 경로면 plain, 아니면 `++`(add-or-override)다. 구조화 편집의
// `diffToOverrides` 와 같은 규칙이다. Hydra 는 없는 키의 plain override 를 compose 에서 거절한다.
// `default_fine_tune` 처럼 checkpoint config 위에 겹치는 부분 config 는 노브 키를 선언하지 않는다.
// 켠 노브는 적용되어야 하므로 행을 빼지 않는다. 본문을 모르면 두 경우 모두 받는 `++` 를 쓴다.
function knobOverrideKey(configData: JsonObject | null, path: string): string {
  const declared = configData !== null && getAtPath(configData, path) !== undefined;
  return formatOverrideKey(declared ? "set" : "force", path);
}

// 폼의 해상도 값이 제출될 본문의 값과 같은지. 본문을 읽을 수 없거나 본문에 값이 없으면
// "다르다"로 본다. 그 경우 폼 값이 실제로 결과를 바꾸므로 override 로 보내야 한다.
function imageSizeMatchesConfig(
  configData: JsonObject | null,
  imageSize: NonNullable<SubmitConfigState["imageSize"]>,
): boolean {
  const configSize = configData === null ? null : imageSizeAt(configData);
  return (
    configSize !== null &&
    configSize.height === imageSize.height &&
    configSize.width === imageSize.width
  );
}

/**
 * "마지막 체크포인트만"이 보낼 저장 주기. 끈 상태이거나 학습 길이를 읽을 수 없으면 `null`.
 *
 * 길이는 **제출될 값**에서 읽는다. 사용자가 폼에 적은 `train.budget.total` override 행이 있으면
 * 그 행을 쓰고(override 가 본문보다 우선한다), 없으면 제출될 본문(직접 편집 중이면 편집본)의 값을 쓴다.
 * 값을 폼에 고정하지 않는 이유도 같다. 학습 길이를 나중에 바꾸면 이전 주기가 남아서 알림 없이
 * 체크포인트가 여러 개 저장된다.
 */
export function saveOnlyLastCheckpointSaveEvery(config: SubmitConfigState): number | null {
  if (!config.saveOnlyLastCheckpoint) {
    return null;
  }
  const row = config.overrides.find((pair) => pair.key.trim() === BUDGET_TOTAL_KEY);
  // `30k`·`7.5k` 같은 접미사 형태가 실제로 쓰이므로 상류와 같은 규칙으로 읽는다.
  if (row !== undefined) {
    return saveEveryNEvalsForLastOnly(parseCount(row.value));
  }
  const parsed = parseRecipe(activeConfigYaml(config));
  if (parsed.errors.length > 0) {
    return null;
  }
  const raw = recipeValueAt(parsed.data, BUDGET_TOTAL_KEY);
  return saveEveryNEvalsForLastOnly(raw === null ? null : parseCount(raw));
}

// 전용 노브가 소유하는 자원 키. 목록에서는 숨기고 노브가 값을 정한다. 한 값의 입력 위치를
// 하나로 두어서 노브와 목록이 달라지지 않게 한다. 이 키들은 1급 필드로 보내므로, 같은 키가 `extra`
// 에 남아 있으면 BE 가 출처가 둘이라며 **거부한다**. 그래서 숨기는 것은 표기 정리가 아니라
// 제출 가능 조건이다.
export const ALLOW_SPOT_EXTRA_KEY = "allow_spot";
export const MIN_LIFETIME_EXTRA_KEY = "min_remaining_lifetime_seconds";
const MANAGED_EXTRA_KEYS: readonly string[] = [ALLOW_SPOT_EXTRA_KEY, MIN_LIFETIME_EXTRA_KEY];

export function isManagedExtraKey(key: string): boolean {
  return MANAGED_EXTRA_KEYS.includes(key.trim());
}

// 스팟 노브의 3-상태 ↔ 화면 선택지. 단일 선택 컨트롤은 값을 문자열로 다루므로, `null`(클러스터
// 기본)까지 왕복하려면 상태마다 고유한 value 가 필요하다. `""` 로 비우면 "고르지 않음"과 "기본을
// 고름"이 같은 값이 되어 세 번째 상태가 사라진다.
export const ALLOW_SPOT_OPTIONS: ReadonlyArray<{
  value: string;
  allowSpot: boolean | null;
  label: string;
  hint: string;
}> = [
  {
    value: "allow",
    allowSpot: true,
    label: "허용",
    hint: "스팟 노드에서도 실행합니다. 저렴하지만 노드가 회수될 수 있습니다.",
  },
  {
    value: "deny",
    allowSpot: false,
    label: "미허용",
    hint: "스팟 노드를 쓰지 않습니다. 클러스터 기본값이 허용이어도 이 값이 이깁니다.",
  },
  {
    value: "cluster_default",
    allowSpot: null,
    label: "클러스터 기본",
    hint: "제출에 값을 싣지 않아 클러스터 설정이 정합니다. 미지정으로 제출된 Session을 재제출할 때 원본 상태입니다.",
  },
];

export function allowSpotChoiceValue(allowSpot: boolean | null): string {
  // 세 상태가 모두 목록에 있으므로 폴백에는 도달하지 않는다. 목록을 줄이면 여기서 드러난다.
  return ALLOW_SPOT_OPTIONS.find((option) => option.allowSpot === allowSpot)?.value ?? "deny";
}

export function allowSpotFromChoiceValue(value: string): boolean | null {
  return ALLOW_SPOT_OPTIONS.find((option) => option.value === value)?.allowSpot ?? null;
}

export function allowSpotChoiceLabel(allowSpot: boolean | null): string {
  return ALLOW_SPOT_OPTIONS.find((option) => option.allowSpot === allowSpot)?.label ?? "미허용";
}

export function allowSpotChoiceHint(allowSpot: boolean | null): string {
  return ALLOW_SPOT_OPTIONS.find((option) => option.allowSpot === allowSpot)?.hint ?? "";
}

// 노드 잔여 수명 하한. 상류가 초 단위 값을 받아 노드의 remaining-lifetime-tier 라벨과 비교해 후보를
// 거른다(`gpu_placement.py::_resource_allows_node`). 값은 tier 경계와 정확히 같게 둔다. 경계를
// 넘으면 상류가 한 단계 위 tier 를 요구해서 후보가 알림 없이 줄어든다(`_allowed_lifetime_tiers`).
//
// `0` 은 하한 없음(lt-4h 노드까지 허용)이다. 값이 없는 것은 하한 없음이 아니다. 세 렌더러가 모두
// `_resources_with_default_lifetime` 으로 4시간을 주입한다. 그래서 폼은 값을 항상 명시한다.
export const MIN_LIFETIME_SECONDS_DEFAULT = 4 * 3600;
export const MIN_LIFETIME_OPTIONS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 0, label: "하한 없음" },
  { seconds: 4 * 3600, label: "4시간 이상" },
  { seconds: 12 * 3600, label: "12시간 이상" },
  { seconds: 24 * 3600, label: "1일 이상" },
  { seconds: 48 * 3600, label: "2일 이상" },
  { seconds: 5 * 24 * 3600, label: "5일 이상" },
  { seconds: 7 * 24 * 3600, label: "7일 이상" },
];

export function minLifetimeLabel(seconds: number): string {
  return MIN_LIFETIME_OPTIONS.find((option) => option.seconds === seconds)?.label ?? `${seconds}초`;
}

// key-value 행 → JsonObject. 빈 key 는 버리고, 값은 recipe parseListOrScalar 로 타입을 해석한다
// (숫자·불리언·null·문자열 + "[3, 480, 640]" 형 리스트). 지수표기(1e-5)도 숫자로 정확히 읽는다.
export function keyValuePairsToObject(pairs: readonly KeyValuePair[]): JsonObject {
  const result: JsonObject = {};
  for (const { key, value } of pairs) {
    const trimmedKey = key.trim();
    if (trimmedKey === "") {
      continue;
    }
    result[trimmedKey] = parseListOrScalar(value);
  }
  return result;
}

// 폼 → 제출 payload. 호출 전에 canSubmitForm 으로 게이트한다(train 입력이 없으면 방어적으로 throw).
export function buildSubmitPayload(
  form: SubmitFormState,
  isaacEvaluation?: IsaacEvaluationReq,
): SubmitTrainingSessionReq {
  if (!hasTrainDataset(form.datasets)) {
    throw new Error("at least one train dataset is required");
  }
  const displayName = form.displayName.trim();
  return {
    datasets: buildDatasetSelectionReqs(form),
    target_dataset_index: buildTargetDatasetIndex(form),
    learning_core: { kind: "branch", ref: learningCoreRef(form) },
    config: buildConfigReq(form.config, sigmaDirectEditOverrides(form)),
    resources: buildResourcesReq(form),
    init: buildInitReq(form),
    resume_on_retry: buildResumeOnRetry(form),
    training_workspace_id: form.workspaceId,
    training_experiment_id: form.experimentId,
    training_experiment_name: form.experimentName ?? undefined,
    display_name: displayName === "" ? undefined : displayName,
    channel_selection: form.channelSelection ?? undefined,
    isaac_evaluation: isaacEvaluation,
    notifications: buildNotificationOverride(form.notifyOnFinish),
  };
}

// 폼의 입력 목록 → wire. 제출과 미리보기가 같은 매핑을 써야 화면의 diff 와 실제 제출물이 같다.
// weight 는 지정되었을 때만 보낸다. 키가 없어야 training 이 균등 배분으로 정한다.
//
// weight 는 target 과 달리 train 개수 게이트를 두지 않는다. 노브가 숨겨져도(train 1개) 폼에 남은
// 값을 그대로 보내고 확정 요약도 같은 값을 표시한다. BE 쪽 게이트가 없으므로 화면과 제출물이
// 어긋나지 않는다. target 만 buildTargetDatasetIndex 로 처리하는 이유가 이 차이다.
export function buildDatasetSelectionReqs(form: SubmitFormState): DatasetSelectionReq[] {
  return form.datasets.map((dataset) => ({
    dataset_uuid: dataset.uuid,
    role: dataset.role,
    ...(dataset.weight === null ? {} : { weight: dataset.weight }),
    ...(dataset.annotationSetUuid === null
      ? {}
      : { annotation_set_uuid: dataset.annotationSetUuid }),
  }));
}

// 학습 타깃의 wire 값. train 이 2개 이상일 때만 뜻이 있다(BE target_index_needs_two_train).
// 제출·검증이 같은 규칙을 써야 "검증이 통과하면 제출도 통과"가 성립한다.
//
// 확정 요약도 이 함수를 거친다. train 축이 줄어 게이트가 닫히면 폼에 남은 targetDatasetIndex
// 는 제출되지 않는다. 그래서 요약이 폼 값을 직접 읽으면 화면만 target 을 표시한다.
export function buildTargetDatasetIndex(form: SubmitFormState): number | undefined {
  return canSelectTargetDataset(form.datasets) ? (form.targetDatasetIndex ?? undefined) : undefined;
}

// 학습 시작점의 wire 값. 값이 없으면 BE 가 init_strategy=scratch 로 제출한다(기본 동작).
// Policy 를 고르지 않은 상태도 값 없음으로 보낸다. 빈 uuid 는 검증·제출 양쪽에서 422 라 리포트도 받을 수 없다.
// 제출은 isInitComplete 가 따로 막는다.
//
// retrain 은 체크포인트 URI 가 아니라 소스 세션 id 를 보낸다. BE 가 스코프 게이트 뒤에서
// 상류의 최신 raw 체크포인트를 해석한다.
function buildInitReq(form: SubmitFormState): TrainingInitReq | undefined {
  const init = form.init;
  if (init.strategy === "retrain") {
    return { strategy: "finetune_from_checkpoint", source_session_id: init.source.id };
  }
  return init.strategy === "finetune" && init.policy !== null
    ? { strategy: "finetune_from_policy", policy_id: init.policy.id }
    : undefined;
}

// 재시도 시 이어서 학습의 wire 값. **노브를 숨기는 것만으로는 부족해서 payload 에서 한 번 더 false 로 바꾼다.**
// 리소스 섹션에서 켠 뒤 섹션 nav 로 돌아가 시작점을 finetune 으로 바꾸면 켜 둔 값이 그대로 남는다.
// 상류는 그 조합을 거절하지 않고 경고 없이 무시하므로, 잘못된 값이 전달되어도 아무도 알려주지 않는다.
// 확인 단계 요약도 이 함수를 쓴다. 요약이 폼 상태를 직접 읽으면 "켜짐"으로 표시하고 payload 는
// false 로 보내는 어긋남이 생긴다.
export function buildResumeOnRetry(form: SubmitFormState): boolean {
  return form.init.strategy === "scratch" && form.resumeOnRetry;
}

// 전용 노브는 1급 필드로 보낸다. 읽기(`recipe.resources`)와 같은 형태라 재제출이 읽은 값을
// 그대로 다시 보낼 수 있다. 같은 키를 `extra` 에도 넣으면 BE 가 출처가 둘이라며 거부한다. 그래서 목록에
// 남은 소유 키는 여기서 거른다(노브가 값의 단일 입력 위치다).
function buildResourcesReq(form: SubmitFormState): ResourcesReq {
  const extra = keyValuePairsToObject(form.resourceExtra);
  for (const key of MANAGED_EXTRA_KEYS) {
    delete extra[key];
  }
  return {
    nodes: form.nodes,
    gpus_per_node: form.gpusPerNode,
    // "클러스터 기본을 따름"은 키를 빼서 표현한다. 명시 false 로 합치면 상류 기본이 허용인
    // 클러스터에서 스팟이 알림 없이 꺼진다.
    ...(form.allowSpot === null ? {} : { allow_spot: form.allowSpot }),
    min_remaining_lifetime_seconds: form.minLifetimeSeconds,
    extra,
  };
}

// 미리보기·경고 검사가 함께 쓰는 config 조립. buildConfigReq 와 달리 예외를 던지지 않는다. 두 경로
// 모두 불완전한 입력에도 결과를 내야 하고, 실패는 경고로 돌아온다. 편집 여부는 제출과 같은 규칙으로
// 반영한다. 그렇지 않으면 화면은 레포 원본 기준으로 결과를 표시하고 제출은 편집본으로 전달된다.
function buildReadOnlyConfigReq(
  config: SubmitConfigState,
  directEditRows: KeyValuePair[],
): ConfigReq {
  const overrides = keyValuePairsToObject([
    ...effectiveConfigOverridePairs(config),
    ...directEditRows,
  ]);
  if (!hasConfigEdit(config)) {
    return { source: "repo_config", name: config.configName, overrides };
  }
  return {
    source: "inline_yaml",
    name: config.configName,
    inline_yaml: activeConfigYaml(config),
    overrides,
  };
}

// 폼 → override 미리보기 요청. target_dataset_index 는 보내지 않는다. 주입은 입력들의 교집합
// 채널로 정해지므로 타깃과 관계없다.
export function buildPreviewPayload(form: SubmitFormState): PreviewOverridesReq {
  return {
    datasets: buildDatasetSelectionReqs(form),
    learning_core: { kind: "branch", ref: learningCoreRef(form) },
    config: buildReadOnlyConfigReq(form.config, sigmaDirectEditOverrides(form)),
    channel_selection: form.channelSelection,
  };
}

// 폼 → 경고 검사 요청. 제출 payload 와 같은 재료(입력·타깃·config·리소스)를 보내야 화면의
// 경고와 submit 이 로그로 남기는 경고가 같은 결과가 된다. workspace·experiment·isaac 은 검사
// 대상이 아니다.
export function buildValidatePayload(form: SubmitFormState): ValidateTrainingSessionReq {
  return {
    datasets: buildDatasetSelectionReqs(form),
    target_dataset_index: buildTargetDatasetIndex(form),
    learning_core: { kind: "branch", ref: learningCoreRef(form) },
    config: buildReadOnlyConfigReq(form.config, sigmaDirectEditOverrides(form)),
    resources: buildResourcesReq(form),
    init: buildInitReq(form),
    channel_selection: form.channelSelection ?? undefined,
  };
}

// 편집이 없으면 repo_config + overrides 를 그대로 보낸다. 무편집 제출의 wire 는 바뀌지 않는다. 편집이 있으면
// inline_yaml 로 전환해서 본문 전체를 보낸다. override 로 환산하지 않는다. 키 삭제·구조 변경·주석을
// 그 형식으로 표현할 수 없어서 정보가 손실된다. RandomBackground off 는 두 경우 모두 자동 delete override 로
// 같은 계약 안에서 표현한다.
function buildConfigReq(config: SubmitConfigState, directEditRows: KeyValuePair[]): ConfigReq {
  const automaticRows = automaticConfigOverrides(config);
  if (!config.randomBackgroundEnabled && randomBackgroundDeleteRows(config).length === 0) {
    throw new Error(
      "config 본문에서 RandomBackground transform을 찾지 못했습니다. YAML을 확인하거나 체크박스를 다시 켜 주세요.",
    );
  }
  const overrides = keyValuePairsToObject([
    ...config.overrides,
    ...automaticRows,
    ...directEditRows,
  ]);
  if (!hasConfigEdit(config)) {
    return { source: "repo_config", name: config.configName, overrides };
  }
  return {
    source: "inline_yaml",
    name: config.configName,
    inline_yaml: activeConfigYaml(config),
    overrides,
  };
}

// 본문에만 기록한 shape는 BE mux 재조립이 Dataset 값으로 바꿀 수 있다.
// 직접 편집으로 넘어간 wrist 값도 명시적 override로 전달해 사용자의 수정을 보존한다.
function sigmaDirectEditOverrides(form: SubmitFormState): KeyValuePair[] {
  const state = form.sigmaDefaults;
  if (form.config.editedYaml === null || state === undefined) return [];
  const edited = parseRecipe(form.config.editedYaml);
  if (edited.errors.length > 0) return [];
  const selected = form.channelSelection?.observation_image;
  const wrists = [
    ...SIGMA_WRISTS.filter(
      (wrist) =>
        state.wrists[wrist] !== undefined && (selected == null || selected.includes(wrist)),
    ),
    ...SIGMA_LEGACY_WRISTS.filter((wrist) => state.removedWrists[wrist] !== undefined),
  ];
  return wrists.map((wrist) => {
    const path = `${SIGMA_CAMERA_PATH}.${wrist}`;
    const value = getAtPath(edited.data, path);
    return {
      key: value === undefined ? `~${path}` : `++${path}`,
      value: value === undefined ? "" : typeof value === "string" ? value : JSON.stringify(value),
    };
  });
}
